import type { MediaRow } from '@/routes/media/types';
import type { PublishStatus } from '@/components/editor/PublishingSidebar';
import type { TranslatedValue } from '@/lib/forms/translatable';

/**
 * docs/09-BUILD-PLAN.md Phase 6 step 9 — pages, page sections and modals.
 *
 * Hand-written for the reason `lib/api/resource.ts` sets out: Scramble types a
 * translatable field as `{[key: string]: unknown}`.
 */

/* ── docs/03-DATA-MODEL.md §7.1's blocks ─────────────────────────────── */

/** The ten types, in the order the "add a block" menu offers them. */
export const BLOCK_TYPES = [
  'rich_text',
  'image',
  'gallery',
  'document_list',
  'accordion',
  'table',
  'quote',
  'embed',
  'cta',
  'stats',
] as const;

export type BlockType = (typeof BLOCK_TYPES)[number];

export const IMAGE_WIDTHS = ['normal', 'wide', 'full'] as const;
export const GALLERY_LAYOUTS = ['grid', 'carousel'] as const;
export const CTA_VARIANTS = ['primary', 'secondary'] as const;
export const EMBED_PROVIDERS = ['youtube', 'vimeo', 'map'] as const;

/**
 * A block, as loosely as the API holds one.
 *
 * `data` is `Record<string, unknown>` rather than a discriminated union on
 * purpose: §7.1's last line is that an unknown type survives and renders
 * nothing, so the editor has to be able to hold a block it does not have a form
 * for and hand it back unchanged. A union would make that unrepresentable.
 */
export interface PageBlock {
  type: BlockType | string;
  data: Record<string, unknown>;
}

/**
 * One image of a `gallery` block.
 *
 * `media_id` is null while the slot is empty — the editor adds a slot and then
 * picks into it, and the server refuses a save with an empty one.
 */
export interface GalleryImage {
  media_id: number | null;
  [key: string]: unknown;
}

/** One item of an `accordion` block. */
export interface AccordionItem {
  title: TranslatedValue;
  body: TranslatedValue;
}

/** One item of a `stats` block. */
export interface StatItem {
  value: string;
  label: TranslatedValue;
}

/* ── pages ───────────────────────────────────────────────────────────── */

export interface PageRow {
  id: number;
  title: TranslatedValue;
  slug: TranslatedValue;
  excerpt: TranslatedValue;
  body: PageBlock[];
  /**
   * The library images the blocks reference, so the editor can draw a
   * thumbnail without the media row living inside the block. Indexed by `id`
   * on arrival — the API sends a list, because a JSON resource re-indexes
   * numeric keys and a map would lose them.
   *
   * Only the detail response fills it — the list screen draws no pickers.
   */
  block_media: MediaRow[];
  cover_media_id: number | null;
  cover: MediaRow | null;
  /*
   * `cover_url`, `photo_url` and `logo_url` were removed here.
   *
   * Task #71 moved images onto the media library and the API resources
   * went from a resolved URL string to the media object above. These
   * types kept declaring the old fields, so three list cells went on
   * reading `undefined` and rendering their empty state — the news list
   * showed a placeholder against 676 articles that had a cover, and
   * nothing failed. Use `thumbUrl(row.cover)`.
   */
  og_image_media_id: number | null;
  og_image: MediaRow | null;
  template: string;
  show_toc: boolean;
  parent_id: number | null;
  parent: { id: number; name: TranslatedValue } | null;
  meta_title: TranslatedValue;
  meta_description: TranslatedValue;
  legacy_wp_id: number | null;
  legacy_url: string | null;
  sort_order: number;
  status: PublishStatus;
  published_at: string | null;
  pending_review: boolean;
  translations_available: string[];
  translation_completeness: Record<string, number>;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  created_by: { id: number; name: string } | null;
  updated_by: { id: number; name: string } | null;
}

export interface PageInput {
  title: TranslatedValue;
  slug: TranslatedValue;
  excerpt: TranslatedValue;
  body: PageBlock[];
  cover_media_id: number | null;
  og_image_media_id: number | null;
  template: string;
  show_toc: boolean;
  parent_id: number | null;
  meta_title: TranslatedValue;
  meta_description: TranslatedValue;
  sort_order: number;
  status: PublishStatus;
  published_at: string | null;
  pending_review: boolean;
}

/* ── page sections, docs/05-ADMIN-DASHBOARD.md §5.2 ──────────────────── */

export interface PageSectionRow {
  id: number;
  page_key: string;
  section_key: string;
  heading: TranslatedValue;
  subheading: TranslatedValue;
  data: Record<string, unknown>;
  /** The library images `data` places — for the pickers, indexed by `id`. */
  media: MediaRow[];
  is_visible: boolean;
  sort_order: number;
  translations_available: string[];
  translation_completeness: Record<string, number>;
  created_at: string | null;
  updated_at: string | null;
  created_by: { id: number; name: string } | null;
  updated_by: { id: number; name: string } | null;
}

export interface PageSectionInput {
  heading: TranslatedValue;
  subheading: TranslatedValue;
  data: Record<string, unknown>;
  is_visible: boolean;
  sort_order: number;
}

/** What `GET /admin/page-sections/pages` answers with. */
export interface PageKeySummary {
  page_key: string;
  sections: number;
  hidden: number;
}

/* ── modals, docs/05-ADMIN-DASHBOARD.md §5.3 ─────────────────────────── */

export interface ModalRow {
  id: number;
  key: string;
  title: TranslatedValue;
  lead: TranslatedValue;
  body: TranslatedValue;
  contact_block: ModalContactBlock | null;
  cta_label: TranslatedValue;
  cta_document_id: number | null;
  cta_document: { id: number; title: TranslatedValue } | null;
  cta_url: string | null;
  status: PublishStatus;
  published_at: string | null;
  pending_review: boolean;
  translations_available: string[];
  translation_completeness: Record<string, number>;
  created_at: string | null;
  updated_at: string | null;
  created_by: { id: number; name: string } | null;
  updated_by: { id: number; name: string } | null;
}

/** §4's optional structured footer — the Preparation Centre modal's shape. */
export interface ModalContactBlock {
  address?: TranslatedValue;
  address_line_2?: TranslatedValue;
  phone?: string | null;
  phone_label?: TranslatedValue;
  email?: string | null;
}

export interface ModalInput {
  title: TranslatedValue;
  lead: TranslatedValue;
  body: TranslatedValue;
  contact_block: ModalContactBlock | null;
  cta_label: TranslatedValue;
  cta_document_id: number | null;
  cta_url: string | null;
  status: PublishStatus;
  published_at: string | null;
  pending_review: boolean;
}
